// The one place the two classification axes become words on a screen: the
// commodity-class filter chips, the material-type filter select, the per-row
// badge, and the plain-language lines under each option in the pickers.
// Catalog, Inventory, the New Item modal and the PO quick-mint all read from
// here, so they cannot drift into four vocabularies for two columns.
//
// Replaces itemTypeDisplay.tsx. The file was renamed because item_type no
// longer exists (migration 016 dropped it), and a module named for a dead
// column is how the next person gets confused.
//
// TWO AXES, deliberately (see Receiving/types.ts and migration 016):
//   material_type   WHERE in the build chain. SAP's standard vocabulary, so
//                   the labels carry the SAP code: a person who reads "ROH" in
//                   a vendor system or a textbook finds the same word here.
//   commodity_class WHAT KIND of thing, and therefore whose supplier base.
//
// ── BADGE RULE, REVISED 2026-08-03 ──────────────────────────────────────────
// BOTH axes now get a column on the catalog list, each rendered as a badge.
//
// The original rule was "badge commodity_class, never material_type", and its
// reasoning is preserved below because it was RIGHT AT THE TIME: every one of
// the 33 rows read 'raw_material', and a badge identical on every row is chrome.
//
// What changed is the premise, not the principle. The quote-to-PO lane mints
// trading goods and equipment (migration 019) from supplier quotations, so
// material_type genuinely varies now — and a column that varies is information.
// The other half of that old note said material_type "surfaces in the filter and
// on the row's own editor instead"; the editor did not exist, which is how an
// item could be mis-filed with no way to see it, let alone fix it.
//
// ORIGINAL REASONING, kept because the principle still governs: do not badge a
// value that is the same everywhere. If a future axis is added and every row
// shares one value, it belongs in the filter, not on the row.
//
// commodity_class is what distinguishes rows at a glance and it is what the
// chips filter on, so every row carries it; an unbadged row would silently mean
// "whatever the majority class is", which is the exact failure the old
// item_type default created. But UNLIKE the previous five-value rule, the badge
// is ONE neutral style rather than a color per value: at ten values the tints
// start colliding (teal against emerald against sky) and become a legend nobody
// memorizes. The text does the work.
//
// SUPERSEDED, kept for the reasoning: "material_type is not badged because it
// is the secondary axis and, today, every single row reads 'raw_material'. A
// badge identical on all 33 rows is chrome, not information. It surfaces in the
// filter and on the row's own editor instead." True until purchasing started
// bringing in things Firefly does not build. See the revision at the top.

import React from 'react';
import { FilterSelect, Segmented } from '../components/FilterBar';
import { Select } from '../components/ui/Select';
import { Tag } from '../components/ui/Tag';
import { Tooltip } from '../components/ui/Tooltip';
import type { MaterialType, CommodityClass } from '../Receiving/types';

export interface OptionMeta {
  /** Full label for a picker. Carries the SAP code for material types. */
  label: string;
  /** Short label for a filter chip or a compact select. */
  short: string;
  /** One line, plain language. What actually goes in this bucket. */
  hint: string;
  // ── The two fields below exist for ClassificationGuide.tsx ────────────────
  // A hint fits under a select and answers "roughly what is this". It does not
  // answer the question people actually stall on, which is always at a BOUNDARY:
  // bare board or assembled board, component or electromechanical, mechanical or
  // hardware. So every value carries concrete examples and the neighbor it gets
  // confused with.
  //
  // REQUIRED, not optional, on purpose: TypeScript then refuses to compile a new
  // classification value that nobody has written a definition for, which is the
  // only reliable way to stop the guide silently going stale.
  /** Three or four real things from this shop that live in this bucket. */
  examples: string;
  /** The neighboring bucket this gets mistaken for, and how to tell them apart. */
  notThis: string;
}

// ── AXIS 1: material_type ───────────────────────────────────────────────────

export const MATERIAL_TYPES: MaterialType[] = [
  'raw_material',
  'semi_finished',
  'finished_good',
  'trading_good',
  'operating_supply',
];

export const MATERIAL_TYPE_META: Record<MaterialType, OptionMeta> = {
  raw_material: {
    label: 'Raw material (ROH)',
    short: 'Raw material',
    hint: 'bought from a vendor and consumed into what we build',
    examples: 'screws, connectors, bare boards, wire, a bought-in tablet that goes into a kit',
    notThis: 'If we built it here rather than bought it, it is not raw material.',
  },
  semi_finished: {
    label: 'Semi-finished (HALB)',
    short: 'Semi-finished',
    hint: 'sent out or built partway, then worked on again here',
    examples: 'a panel that goes out for conformal coat and comes back, a sub-assembly waiting on final fit',
    notThis: 'A unit that is finished and ready to ship is a finished good, not semi-finished.',
  },
  finished_good: {
    label: 'Finished good (FERT)',
    short: 'Finished good',
    hint: 'built here and sold as it is',
    examples: 'a complete drone, an assembled charger, a ground station kit we build',
    notThis: 'Something we buy complete and resell untouched is a trading good.',
  },
  trading_good: {
    label: 'Trading good (HAWA)',
    short: 'Trading good',
    hint: 'bought and resold without changing it',
    examples: 'a tablet or a case passed straight through to a customer at a markup',
    notThis: 'If it gets built into something else here, it is raw material instead.',
  },
  operating_supply: {
    label: 'Operating supply (HIBE)',
    short: 'Operating supply',
    hint: 'used up making things but never part of the product: solder, flux',
    examples: 'solder, flux, isopropyl, gloves, tape, abrasives',
    notThis: 'If any of it ends up inside the product that ships, it is raw material.',
  },
};

// ── AXIS 2: commodity_class ─────────────────────────────────────────────────

export const COMMODITY_CLASSES: CommodityClass[] = [
  'pcb',
  'pcba',
  'electronic_component',
  'electromechanical',
  'cable_harness',
  'mechanical',
  'hardware_fastener',
  'battery',
  'material',
  'packaging',
  // LAST in the list on purpose. The order here drives the picker and the filter
  // chips, and the ten before it are the component-level vocabulary the catalog
  // is mostly made of. A whole bought-in device is the exception, and putting it
  // first would invite it as the lazy answer for anything hard to place.
  'equipment',
];

export const COMMODITY_CLASS_META: Record<CommodityClass, OptionMeta> = {
  pcb: {
    label: 'Bare board (PCB)',
    short: 'Bare boards',
    hint: 'an empty circuit board, before any parts go on it',
    examples: 'a bare AP board, a bare ESC board, a blank LED panel',
    notThis: 'Once components are fitted it is an assembled board (PCBA).',
  },
  pcba: {
    label: 'Assembled board (PCBA)',
    short: 'Assembled boards',
    hint: 'a circuit board with its parts fitted',
    examples: 'a populated AP board, a finished ESC, an LED panel with its drivers on',
    notThis: 'Before the parts go on, the same board is a bare board (PCB).',
  },
  electronic_component: {
    label: 'Electronic component',
    short: 'Electronic',
    hint: 'a single electrical part: chip, connector, antenna',
    examples: 'resistors, capacitors, MCUs, headers, GPS antennas, regulators',
    notThis: 'If it moves, switches or makes noise, it is electromechanical.',
  },
  electromechanical: {
    label: 'Electromechanical',
    short: 'Electromechanical',
    hint: 'electrical parts that move or switch: motors, buttons, fans',
    examples: 'motors, fans, buzzers, tactile switches, relays, solenoids',
    notThis: 'A part with no moving element is an electronic component.',
  },
  cable_harness: {
    label: 'Cable or harness',
    short: 'Cable & harness',
    hint: 'wire assemblies and terminated cables',
    examples: 'a made-up battery harness, a terminated JST lead, a USB cable',
    notThis: 'Wire bought on a spool by the meter is bulk material until it is terminated.',
  },
  mechanical: {
    label: 'Mechanical',
    short: 'Mechanical',
    hint: 'formed, machined, moulded or printed parts: frames, domes, brackets',
    examples: 'arm tubes, printed domes, machined brackets, moulded shells',
    notThis: 'Off-the-shelf screws and standoffs are hardware, even though they are metal.',
  },
  hardware_fastener: {
    label: 'Hardware and fasteners',
    short: 'Hardware',
    hint: 'screws, standoffs, grommets, mounting bits',
    examples: 'M3 screws, standoffs, washers, grommets, cable ties, heat-set inserts',
    notThis: 'A part drawn and made for us is mechanical, even if it bolts things together.',
  },
  battery: {
    label: 'Battery',
    short: 'Battery',
    hint: 'cells and packs, which ship and store under their own rules',
    examples: '18650 cells, finished flight packs, coin cells',
    notThis: 'The harness that connects a pack is a cable, not a battery.',
  },
  material: {
    label: 'Bulk material',
    short: 'Bulk material',
    hint: 'stock used by length or weight: wire, filament, adhesive',
    examples: 'spooled wire, 3D printer filament, adhesives, potting compound, shrink tube',
    notThis: 'Once it is cut and terminated into an assembly it becomes a cable or harness.',
  },
  packaging: {
    label: 'Packaging',
    short: 'Packaging',
    hint: 'boxes, foam, labels, anti-static bags',
    examples: 'shipping boxes, foam inserts, anti-static bags, box labels, pallet wrap',
    notThis: 'A flight case sold to a customer as part of a kit is a trading good, not packaging.',
  },
  equipment: {
    label: 'Equipment or device',
    short: 'Equipment',
    hint: 'a complete working unit, bought whole rather than built here',
    examples: 'a handheld controller, a rugged tablet, a bench power supply, a charger bought complete',
    // The boundary that matters, and the one this class was added for: it is
    // about WHOLENESS, not about who made it. "We bought it" is the other axis.
    notThis: 'A part that goes inside something else is a component, however finished it looks on its own.',
  },
};

// ── Filter controls ─────────────────────────────────────────────────────────

export const COMMODITY_CLASS_CHIPS: { value: '' | CommodityClass; label: string }[] = [
  { value: '', label: 'All' },
  ...COMMODITY_CLASSES.map((c) => ({ value: c, label: COMMODITY_CLASS_META[c].short })),
];

/**
 * Eleven segments including All, on the house segmented control. They fit one
 * line at laptop width using the short labels and wrap to a tidy second row
 * below that. Deliberately NOT collapsed behind a "more" control: a hidden
 * filter option is a filter nobody uses, and the whole point of the split is
 * that this axis is the one people browse by.
 */
export const CommodityClassFilterChips: React.FC<{
  value: '' | CommodityClass;
  onChange: (v: '' | CommodityClass) => void;
}> = ({ value, onChange }) => (
  <Segmented
    label="Filter by commodity class"
    options={COMMODITY_CLASS_CHIPS.map((chip) => ({ key: chip.value, label: chip.label }))}
    value={value}
    onChange={(key) => onChange(key as '' | CommodityClass)}
  />
);

/**
 * The secondary axis, as a compact select rather than a second chip row. Two
 * chip rows would read as one wrapped bar and hide which filter is which; a
 * labeled select says "different question" without spending vertical space.
 */
export const MaterialTypeFilterSelect: React.FC<{
  value: '' | MaterialType;
  onChange: (v: '' | MaterialType) => void;
  id?: string;
}> = ({ value, onChange, id = 'material-type-filter' }) => (
  <FilterSelect id={id} label="Build stage" value={value} onChange={(v) => onChange(v as '' | MaterialType)}>
    <option value="">Any</option>
    {MATERIAL_TYPES.map((t) => (
      <option key={t} value={t}>{MATERIAL_TYPE_META[t].short}</option>
    ))}
  </FilterSelect>
);

/**
 * Every classified row gets its commodity class as a tag. A row with no
 * recognizable value renders nothing rather than inventing one: an honest
 * display of something the database no longer permits to be blank, but that a
 * stale cached response could still carry.
 */
export const CommodityClassBadge: React.FC<{
  commodityClass: CommodityClass | null | undefined;
  /** Drop the leading margin when the badge is alone in its own cell. */
  inline?: boolean;
}> = ({ commodityClass, inline = true }) => {
  const meta = commodityClass ? COMMODITY_CLASS_META[commodityClass] : undefined;
  if (!meta) return null;
  return (
    <Tag size="sm" className={`align-middle ${inline ? 'ml-2' : ''}`}>
      {meta.short.toLowerCase()}
    </Tag>
  );
};

/** The same treatment for axis 1, for the catalog's own Build stage column. */
export const MaterialTypeBadge: React.FC<{
  materialType: MaterialType | null | undefined;
}> = ({ materialType }) => {
  const meta = materialType ? MATERIAL_TYPE_META[materialType] : undefined;
  if (!meta) return null;
  return (
    <Tag size="sm" className="align-middle">
      {meta.short.toLowerCase()}
    </Tag>
  );
};

// ── The editable cell ───────────────────────────────────────────────────────
//
// Until now neither axis could be changed anywhere: parts-catalog-router has had
// both in CATALOG_PATCHABLE_FIELDS since 016 with a comment saying the controls
// were owed, and correcting a mis-classified item meant a PATCH by hand.
//
// BADGE UNTIL CLICKED, rather than a select sitting in every row. Two selects
// times a hundred rows is a wall of chrome on a table whose job is scanning, and
// every one of them is a stray scroll or keypress away from silently re-filing
// an item. A badge that becomes a select when you mean it keeps the list
// readable and makes the edit deliberate. It also keeps the look Mannie asked
// for: "a badge in the column would look nice, the way it looks now."
//
// Escape cancels, blur closes, and choosing a value commits and closes. There is
// no explicit save button because there is nothing else on the control to get
// wrong — one field, one value, already validated by the option list.

/**
 * One classification, shown as a badge and edited in place.
 *
 * @param value    current value, or null on a row that predates the column
 * @param options  the axis's values, in picker order
 * @param meta     that axis's label/short map
 * @param onChange commits; the caller PATCHes and owns the error toast
 */
export function ClassificationCell<T extends string>({
  value,
  options,
  meta,
  canEdit,
  label,
  rowLabel,
  onChange,
}: {
  value: T | null | undefined;
  options: T[];
  meta: Record<string, OptionMeta>;
  canEdit: boolean;
  label: string;
  rowLabel: string;
  onChange: (next: T) => void;
}) {
  const [editing, setEditing] = React.useState(false);
  const shown = value ? meta[value] : undefined;

  if (!canEdit) {
    return shown ? (
      <Tag size="sm">{shown.short.toLowerCase()}</Tag>
    ) : (
      <span className="text-on-surface-disabled">—</span>
    );
  }

  if (editing) {
    return (
      <Select
        variant="inline"
        size="sm"
        autoFocus
        defaultOpen
        label={`${label}, ${rowLabel}`}
        value={value || null}
        placeholder="Select…"
        options={options.map((o) => ({ key: o, label: meta[o].short }))}
        onChange={(next) => onChange(next as T)}
        onClose={() => setEditing(false)}
        onBlur={() => setEditing(false)}
      />
    );
  }

  return (
    <Tooltip label={shown ? `${shown.label}: ${shown.hint}` : `Set ${label.toLowerCase()}`}>
      <Tag
        size="sm"
        onClick={() => setEditing(true)}
        aria-label={`${label}, ${rowLabel}: ${shown ? shown.short : 'not set'}. Change it.`}
      >
        {shown ? shown.short.toLowerCase() : 'set…'}
      </Tag>
    </Tooltip>
  );
}
